// SEO Utilities for Roke Car Sales
import { Car } from '../types';
import { BUSINESS_CONFIG } from '../config/business';

// Update page title dynamically
export const updatePageTitle = (title: string) => {
  document.title = `${title} | ${BUSINESS_CONFIG.businessName} - ${BUSINESS_CONFIG.tagline}`;
};

// Update meta description
export const updateMetaDescription = (description: string) => {
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', description);
  }
};

// Update canonical URL
export const updateCanonicalUrl = (url: string) => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
};

// Generate structured data for a car listing
export const generateCarStructuredData = (car: Car): string => {
  const baseUrl = window.location.origin;
  const carUrl = `${baseUrl}/car/${car.id}`;
  const imageUrl = car.images && car.images.length > 0 ? car.images[0] : `${baseUrl}/default-car.jpg`;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Car",
    "name": `${car.year} ${car.make} ${car.model}`,
    "description": car.description,
    "url": carUrl,
    "image": imageUrl,
    "brand": {
      "@type": "Brand",
      "name": car.make
    },
    "model": car.model,
    "vehicleModelDate": car.year.toString(),
    "mileageFromOdometer": {
      "@type": "QuantitativeValue",
      "value": car.mileage,
      "unitCode": "SMI"
    },
    "fuelType": car.fuelType,
    "vehicleTransmission": car.transmission,
    "vehicleConfiguration": car.condition,
    "color": car.color,
    "offers": {
      "@type": "Offer",
      "price": car.price,
      "priceCurrency": "USD",
      "availability": car.saleStatus === 'available' ? "https://schema.org/InStock" : 
                     car.saleStatus === 'pending' ? "https://schema.org/PreOrder" : 
                     "https://schema.org/OutOfStock",
      "seller": {
        "@type": "AutoDealer",
        "name": BUSINESS_CONFIG.businessName,
        "telephone": `+${BUSINESS_CONFIG.whatsappPhone}`,
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "FL",
          "addressCountry": "US"
        }
      }
    }
  };
  
  return JSON.stringify(structuredData);
};

// Add structured data to page
export const addStructuredData = (structuredData: string) => {
  // Remove existing car structured data
  const existingScript = document.querySelector('script[data-type="car-structured-data"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-type', 'car-structured-data');
  script.textContent = structuredData;
  document.head.appendChild(script);
};

// Generate SEO-friendly car title
export const generateCarSEOTitle = (car: Car): string => {
  const condition = car.condition === 'New' ? 'New' : 'Used';
  return `${condition} ${car.year} ${car.make} ${car.model} for Sale in Florida`;
};

// Generate SEO-friendly car description
export const generateCarSEODescription = (car: Car): string => {
  const condition = car.condition === 'New' ? 'new' : 'used';
  const mileage = new Intl.NumberFormat('en-US').format(car.mileage);
  const price = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(car.price);
  
  return `Find this ${condition} ${car.year} ${car.make} ${car.model} at Roke Car Sales in Florida. ${mileage} miles, ${car.fuelType}, ${car.transmission}. Priced at ${price}. Quality used cars with excellent customer service.`;
};

// Florida-specific location keywords
export const FLORIDA_LOCATIONS = [
  'Miami', 'Orlando', 'Tampa', 'Jacksonville', 'Fort Lauderdale', 
  'Tallahassee', 'St. Petersburg', 'Hialeah', 'Gainesville', 'Coral Springs',
  'Miami-Dade', 'Broward', 'Palm Beach', 'Hillsborough', 'Orange County'
];

// Car-related keywords for SEO
export const CAR_KEYWORDS = [
  'sedan', 'SUV', 'truck', 'hatchback', 'coupe', 'convertible',
  'automatic', 'manual', 'gasoline', 'hybrid', 'electric',
  'certified pre-owned', 'low mileage', 'one owner', 'clean title'
];

// Generate comprehensive keywords for a car
export const generateCarKeywords = (car: Car): string[] => {
  const keywords = [
    // Basic car info
    `${car.year} ${car.make} ${car.model}`,
    `${car.make} ${car.model}`,
    car.make,
    car.model,
    
    // Condition and type
    car.condition.toLowerCase(),
    car.fuelType.toLowerCase(),
    car.transmission.toLowerCase(),
    
    // Location-based
    ...FLORIDA_LOCATIONS.map(location => `${car.make} ${car.model} ${location}`),
    
    // General keywords
    'used cars Florida',
    'pre-owned vehicles',
    'car dealership Florida',
    'quality used cars'
  ];
  
  return keywords;
};